import type { VnextContractRepository } from '../application/VnextContractRepository'
import { contractMatchesDisplayStatus, isVipContract } from '@/features/contracts/domain/contract'
import type { VnextContractListItem, VnextListScope } from '../domain/vnextContract'
import { contractTotalCents } from '../domain/vnextContract'
import {
  demoContractList,
  demoLaurentidesContractId,
  demoLaurentidesOverview,
  demoLocationDetail,
} from './fixtures/vnextDemoData'

/** Même dérivation que la liste Résidentiel : badge, vue rapide et filtre ne se contredisent jamais. */
function scoped(scope: VnextListScope): VnextContractListItem[] {
  const term = scope.search.trim().toLowerCase()
  return demoContractList(scope.kind).filter(
    (item) =>
      (!term || item.number.toLowerCase().includes(term) || item.company.toLowerCase().includes(term)) &&
      (!scope.displayStatus || contractMatchesDisplayStatus(item, scope.displayStatus)) &&
      (!scope.vip || isVipContract(item)),
  )
}

/** Démonstration sans base : aucune écriture réelle, numéros fictifs. */
export function createFixtureVnextContractRepository(): VnextContractRepository {
  let sequence = 15
  return {
    list(filters) {
      const items = scoped(filters)
      const from = (filters.page - 1) * filters.pageSize
      return Promise.resolve({
        items: items.slice(from, from + filters.pageSize),
        total: items.length,
        source: 'fixtures',
      })
    },
    count(scope) {
      return Promise.resolve(scoped(scope).length)
    },
    create(command) {
      const prefix = command.contractType === 'COMMERCIAL' ? 'COM' : 'MLT'
      sequence += 1
      return Promise.resolve({
        contractId: demoLaurentidesContractId,
        number: `${prefix}-${String(sequence).padStart(5, '0')}`,
        totalCents: contractTotalCents(command.pricingMode, command.globalPriceCents, command.locations),
        locationCount: command.locations.length,
      })
    },
    locationsOverview() {
      return Promise.resolve(demoLaurentidesOverview())
    },
    flowOf(reference) {
      const demo = [...demoContractList('MULTIFAMILY'), ...demoContractList('COMMERCIAL')].find(
        (item) => item.number === reference || item.id === reference,
      )
      return Promise.resolve(demo ? { id: demo.id, number: demo.number, flow: 'VNEXT' } : null)
    },
    defaultContact() {
      return Promise.resolve({
        firstName: 'Marie-Ève',
        lastName: 'Gagnon',
        phone: '450 555-0142',
        roleLabel: 'Gestionnaire immobilière',
        isEmergency: true,
      })
    },
    locationDetail(locationId) {
      return Promise.resolve(demoLocationDetail(locationId))
    },
    // Démonstration : rien n'est écrit, l'état de préparation affiché reste celui de la démo.
    updateLocation(command) {
      return Promise.resolve(demoLocationDetail(command.locationId).readiness)
    },
    copySettings(command) {
      return Promise.resolve(command.targetIds.length)
    },
    replaceEntries(locationId) {
      return Promise.resolve(demoLocationDetail(locationId).readiness)
    },
    replaceZones(command) {
      return Promise.resolve({
        readiness: demoLocationDetail(command.locationId).readiness,
        revision: command.expectedRevision + 1,
      })
    },
    zonesRevision() {
      return Promise.resolve(0)
    },
    // Démonstration : aucune conversion réelle; « commencer » renvoie un identifiant fictif.
    conversionState() {
      return Promise.resolve({})
    },
    startConversion() {
      return Promise.resolve('00000000-0000-4000-a000-0000000c0001')
    },
    completeConversion() {
      return Promise.resolve()
    },
    abandonConversion() {
      return Promise.resolve()
    },
    finalizeConversion() {
      return Promise.resolve()
    },
    changeType() {
      return Promise.resolve()
    },
  }
}
